<template>
  <div class="multi-selection-table-container scroll-fire-fox">
    <table
      v-if="!loading && fields.length > 0 && objects.length > 0"
      class="multi-selection-table"
    >
      <tr class="table-header">
        <th class="object-index" />
        <th>
          ID
        </th>
        <th
          v-for="(field, index) in fields"
          :key="index + field.label"
          :class="{ required: field.required }"
        >
          {{ getFieldLabel(field) }}
        </th>
      </tr>
      <tr
        class="table-body"
        v-for="(object, objectIndex) in objects"
        :class="{ selected: selected.includes(objectIndex) }"
      >
        <td class="object-index" @click="select(objectIndex, $event)">
          {{ objectIndex }}
        </td>
        <td class="input-disabled">
          {{ object[initials + "_id"] }}
        </td>
        <td
          v-for="(field, fieldIndex) in fields"
          :key="fieldIndex + field.label"
          :class="editMode ? field.size : ''"
        >
          <div
            class="input-disabled"
            v-if="!editMode && field.input_type !== 'checkbox'"
          >
            {{ getNonEditableValue(object, field) }}
          </div>

          <input
            v-else-if="isStandardInput(field.input_type)"
            :type="getSpecificStandardInputType(field.input_type)"
            :value="getValue(object, field)"
            @change="setValue(objectIndex, field, $event.target.value)"
            :disabled="field.disable"
            class="table-input"
            :class="{
              'required-not-filled': requiredWasMadeEmpty(objectIndex, field)
            }"
          />

          <treeselect
            v-else-if="field.input_type === 'select'"
            :value="getValue(object, field)"
            @input="setValue(objectIndex, field, $event)"
            :options="options[field.select_list_name]"
            :disabled="field.disable"
            :clearable="!field.required"
            class="multi-selection-select"
            :class="{
              'required-not-filled': requiredWasMadeEmpty(objectIndex, field)
            }"
            disableFuzzyMatching
          />

          <treeselect
            v-else-if="field.input_type === 'radio'"
            :value="getValue(object, field)"
            @input="setValue(objectIndex, field, $event)"
            :options="options[field.var_name]"
            :disabled="field.disable"
            :clearable="false"
            class="multi-selection-select"
            :class="{
              'required-not-filled': requiredWasMadeEmpty(objectIndex, field)
            }"
            disableFuzzyMatching
          />

          <div
            v-if="field.input_type === 'checkbox'"
            class="multi-selection-checkbox-container"
            :class="{
              'required-not-filled': requiredWasMadeEmpty(objectIndex, field)
            }"
          >
            <v-checkbox
              :input-value="getValue(object, field)"
              @change="setValue(objectIndex, field, $event)"
              color="accent"
              class="multi-selection-checkbox"
              :ripple="false"
              :disabled="!editMode"
              :false-value="0"
              :true-value="1"
              :hide-details="true"
              disableFuzzyMatching
            />
          </div>
        </td>
      </tr>
    </table>

    <div v-else class="table-warning">
      There are no customers premises discriminated in this building.
    </div>

    <loading-spinner
      :active.sync="loading"
      :is-full-page="false"
      color="accent"
      background-color="transparent"
    />
  </div>
</template>

<script>
import { bus } from "@/main";
import _ from "lodash";
import LoadingSpinner from "vue-loading-overlay";
import { listsMixin } from "@/mixins/listsMixin";

export default {
  name: "CustomerPremisesTable",

  components: { LoadingSpinner },

  mixins: [listsMixin],

  props: {
    /**
     * Layer corresponding to the selected tab.
     */
    layer: {
      type: String,
      required: true
    },
    editMode: {
      type: Boolean,
      required: true
    },
    originalObjects: {
      type: Array,
      required: true
    },
    buildingUuid: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      loadingStructure: false,
      fields: [],
      options: {},
      initials: undefined,

      loadingObjects: false,
      objects: [],
      selected: [],
      numberOfOriginalObjects: 0,

      objectsToRemove: [],
      objectsToAdd: [],
      objectsToEdit: [],

      lastItemClicked: undefined
    };
  },

  computed: {
    /**
     * Determines the endpoint to use to make any requests.
     */
    endpoint() {
      return this.$store.getters.getLayerEndpoint(this.layer);
    },
    /**
     * Determines if the datatable content is loading or not.
     * @return {Boolean}
     */
    loading() {
      return this.loadingStructure || this.loadingObjects;
    },
    /**
     * Determines the number of objects
     * @return {Number}
     */
    numberObjects() {
      return this.objects.length;
    },
    /**
     * Determines the number of objects selected
     * @return {Number}
     */
    numberSelectedObjects() {
      return this.selected.length;
    }
  },

  mounted() {
    this.updateTable();
    this.receiveInstructionsFromToolbar();
    bus.$emit("updateNumberCustomerPremises", this.numberObjects);
    this.updateTotalOfHPs();
  },

  methods: {
    /**
     * Updates the table structure and content.
     */
    updateTable() {
      this.getTableStructure();
      this.getObjects();
    },

    /**
     * Retrieves the current tab object type from the store.
     */
    getTableStructure() {
      this.fields = [];
      this.loadingStructure = true;
      this.$store
        .dispatch("FETCH_OBJECT_FORM_STRUCTURE_BY_OBJECT_TYPE", {
          layer: this.layer,
          needsTabs: false,
          needsFields: true
        })
        .then(data => {
          let formFields = data.fields;
          this.layer = data.layer;
          this.initials = data.initials;
          formFields.forEach(field => {
            if (
              field.multi_select_show &&
              field.form_table_show &&
              (field.hide === 0 || !field.hide)
            ) {
              if (field.input_type === "checkbox") {
                this.getCheckboxFields(field);
              } else {
                if (field.input_type === "select") {
                  this.getSelectOptions(field.select_list_name);
                } else if (field.input_type === "radio") {
                  this.getRadioOptions(field);
                }
                this.fields.push(field);
              }
            }
          });
          this.loadingStructure = false;
        })
        .catch(error => console.log(error));
    },

    /**
     * Gets the label to use for the given field.
     * @param {Object} field
     * @returns {String}
     */
    getFieldLabel(field) {
      if (field.hasOwnProperty("multi_select_label")) {
        return field.multi_select_label;
      } else return field.label;
    },

    /**
     * Obtains the objects of the currently selected layer present in the selected area.
     */
    getObjects() {
      this.selected = [];
      this.objects = [];
      this.loadingObjects = true;

      if (this.originalObjects) {
        this.numberOfOriginalObjects = this.originalObjects.length;
        if (this.numberOfOriginalObjects > 0) {
          let filteredObjects = this.filterObjects(
            this.originalObjects,
            this.layer.includes("base")
          );
          this.objects = JSON.parse(JSON.stringify(filteredObjects));
        }
      }

      this.loadingObjects = false;
    },

    /**
     * Gets only the objects corresponding to the given layer.
     * @param {Array} objectList - list of objects returned by the server
     * @param {Boolean} addIfBlocked - determines if it's the blocked or unblocked objects that are to be added to
     * the current list of objects.
     * @returns {Array}
     */
    filterObjects(objectList, addIfBlocked) {
      let filteredObjects = [];
      let blockedVar = this.initials + "_blocked";
      if (objectList[0].hasOwnProperty(blockedVar)) {
        objectList.forEach(object => {
          if (
            (addIfBlocked && object[blockedVar]) ||
            (!addIfBlocked && !object[blockedVar])
          ) {
            filteredObjects.push(object);
          }
        });
      } else {
        filteredObjects = objectList;
      }
      return filteredObjects;
    },

    /**
     * Objects the UUID value of a given object
     * @param {Object} object
     * @returns {String}
     */
    getObjectUUID(object) {
      return object[this.initials + "_uuid"];
    },

    /**
     * Opens the necessary channels to receive instructions from the toolbar.
     */
    receiveInstructionsFromToolbar() {
      bus.$on("save_customer_premises", promise => {
        promise(this.save());
      });
      bus.$on("add", () => this.add());
      bus.$on("delete", () => this.remove());
      bus.$on("clone", () => this.clone());
      bus.$on("selectAll", () => this.selectAll());
      bus.$on("invertSelection", () => this.invertSelection());
      bus.$on("clearSelection", () => this.clearSelection());
    },

    /**
     * Determines if the user if making a simple select or a bulk select and executes the correct action.
     * @param {Number} index - index of the object clicked
     * @param {Object} e - information about the click event
     */
    select(index, e) {
      if (e.shiftKey) {
        this.bulkSelect(index);
      } else {
        this.toggleObject(index);
      }
      this.lastItemClicked = index;
    },

    /**
     * Allows selecting a group of items in bulk.
     * @param index - index of the last clicked item.
     */
    bulkSelect(index) {
      let previousClick = 0;
      let firstItem;
      let lastItem;
      if (this.lastItemClicked !== undefined) {
        previousClick = this.lastItemClicked;
      }
      if (previousClick === index) {
        this.toggleObject(index);
      } else {
        if (previousClick < index) {
          firstItem = previousClick;
          lastItem = index;
        } else if (previousClick > index) {
          firstItem = index;
          lastItem = previousClick;
        }
        for (let i = firstItem; i <= lastItem; i++) {
          if (!this.selected.includes(i)) {
            this.selected.push(i);
          }
        }
      }
    },

    /**
     * Adds or removes an object index from the selected list
     * @param {number} index -  index of the object in the objects list
     */
    toggleObject(index) {
      let selectedIndex = this.selected.findIndex(
        selected => selected === index
      );
      if (selectedIndex !== -1) {
        this.selected.splice(selectedIndex, 1);
      } else {
        this.selected.push(index);
      }
    },

    /**
     * Adds all objects to the selected list.
     */
    selectAll() {
      this.clearSelection();
      let nrOfObjects = this.objects.length;
      for (let i = 0; i < nrOfObjects; i++) {
        this.selected.push(i);
      }
    },

    /**
     * Inverts the selection, i.e. adds the previously non-selected objects to the list and removes the previously
     * selected from the list.
     */
    invertSelection() {
      let nrOfObjects = this.objects.length;
      for (let i = 0; i < nrOfObjects; i++) {
        this.toggleObject(i);
      }
    },

    /**
     * Removes all objects from the selected list.
     */
    clearSelection() {
      this.selected = [];
    },

    /**
     * Creates a list of the UUIDs of the selected objects.
     */
    getSelectedUUIDs() {
      let selectedUUIDs = [];
      this.selected.forEach(objectIndex => {
        selectedUUIDs.push({
          uuid: this.getObjectUUID(this.objects[objectIndex])
        });
      });
      return selectedUUIDs;
    },

    /**
     * Eliminates the selected objects.
     */
    add() {
      let newObject = {};
      this.fields.forEach(field => {
        let newVarName = field.var_name.replace(this.initials + "_", "");
        if (newVarName !== "id" && newVarName !== "uuid") {
          newObject[newVarName] = undefined;
        }
      });

      this.objects.push(newObject);
      this.objectsToAdd.push(newObject);
    },

    /**
     * Eliminates the selected objects.
     */
    remove() {
      this.selected.sort(function(a, b) {
        return b - a;
      });
      this.selected.forEach(objectIndex => {
        if (objectIndex < this.numberOfOriginalObjects) {
          let uuid = this.getObjectUUID(this.objects[objectIndex]);

          //Add object to array of objects to remove
          this.objectsToRemove.push({ uuid: uuid });

          //Remove object from objectsToEdit if there was updates to the object.
          let editedObjectIndex = this.objectsToEdit.findIndex(
            editedObject => editedObject.uuid === uuid
          );
          if (editedObjectIndex !== -1) {
            this.objectsToEdit.splice(editedObjectIndex, 1);
          }

          this.numberOfOriginalObjects = this.numberOfOriginalObjects - 1;
        } else {
          this.objectsToAdd.splice(
            objectIndex - this.numberOfOriginalObjects,
            1
          );
        }

        this.objects.splice(objectIndex, 1);
      });
      this.selected = [];
    },

    /**
     * Clones the selected objects (creates new objects with the same information)
     */
    clone() {
      this.$store
        .dispatch("CLONE_OBJECTS", {
          endpoint: this.endpoint,
          data: this.getSelectedUUIDs()
        })
        .then(() => {
          this.refresh();
        })
        .catch(error => {
          console.error(error);
        });
    },

    /**
     * Saves the data that was removed, edited and created.
     */
    save() {
      return new Promise((resolve, reject) => {
        this.saveRemovedObjects().then(() => {
          this.saveEditedObjects().then(() => {
            this.saveNewObjects().then(() => {
              resolve();
            });
          });
        });
      });
    },

    saveRemovedObjects() {
      return new Promise((resolve, reject) => {
        if (this.objectsToRemove.length !== 0) {
          this.$store
            .dispatch("DELETE_OBJECTS", {
              endpoint: this.endpoint,
              objects: this.objectsToRemove
            })
            .then(() => {
              resolve();
            })
            .catch(error => {
              console.error(error);
              reject();
            });
        } else {
          resolve();
        }
      });
    },

    saveEditedObjects() {
      return new Promise((resolve, reject) => {
        if (this.objectsToEdit.length !== 0) {
          this.$store
            .dispatch("EDIT_OBJECTS", {
              endpoint: this.endpoint,
              data: this.objectsToEdit
            })
            .then(() => {
              resolve();
            })
            .catch(error => {
              console.error(error);
              reject();
            });
        } else {
          resolve();
        }
      });
    },

    saveNewObjects() {
      this.addUuidToObjectsToAdd();

      return new Promise((resolve, reject) => {
        if (this.objectsToAdd.length !== 0) {
          this.$store
            .dispatch("CREATE_OBJECTS", {
              endpoint: this.endpoint,
              data: this.objectsToAdd
            })
            .then(() => {
              resolve();
            })
            .catch(error => {
              console.error(error);
              reject();
            });
        } else {
          resolve();
        }
      });
    },

    addUuidToObjectsToAdd() {
      this.objectsToAdd.forEach(newObject => {
        newObject.building = {};
        newObject.building.uuid = this.buildingUuid;
      });
    },

    /**
     * Gets the original value of a given variable
     * @param {Object} object - object from which the value is being obtained
     * @param {Object} field - field being analysed
     * @return {String | Number}
     */
    getValue(object, field) {
      return _.get(object, field.var_name);
    },

    /**
     * Getd the feld value in non edition mode.
     * @param {Object} object
     * @param {Object} field
     * @returns {Number | String}
     */
    getNonEditableValue(object, field) {
      let value = this.getValue(object, field);
      if (value === null || value === undefined || value === "") {
        value = "-";
      } else if (field.input_type === "select") {
        value = this.getOptionLabel(field.select_list_name, value);
      } else if (field.input_type === "radio") {
        value = this.getOptionLabel(field.var_name, value);
      }
      return value;
    },

    /**
     * Gets the label corresponding to the value of a given select variable
     * @param {Object} optionList - list being analysed
     * @param id
     * @return {String | Number}
     */
    getOptionLabel(optionList, id) {
      let option = this.options[optionList].find(item => item.id === id);
      if (option !== undefined) {
        return option.label;
      } else return id;
    },

    /**
     * Obtains the the field content of a checkbox option.
     * @param {Object} field - field being analysed
     */
    getCheckboxFields(field) {
      field.checkbox_options.forEach(option => {
        let checkboxField = option;
        checkboxField.input_type = field.input_type;
        this.fields.push(checkboxField);
      });
    },

    /**
     * Obtains the the field content of a checkbox option.
     * @param {Object} field - field being analysed
     */
    getRadioOptions(field) {
      let varName = field.var_name;
      this.options[varName] = [];
      field.radio_options.forEach(option => {
        let radioOption = {
          id: option.value,
          label: option.label
        };
        this.options[varName].push(radioOption);
      });
    },

    /**
     * Obtains the options for a given select list.
     * @param {String} listName - name of the list to use
     */
    getSelectOptions(listName) {
      if (
        !this.options.hasOwnProperty(listName) ||
        this.options[listName].length === 0
      ) {
        let list = this.$store.getters.getListOfOptionsByListName(
          listName,
          [],
          false
        );
        if (list === undefined) {
        } else {
          this.options[listName] = [];
          list.forEach(option => {
            this.options[listName].push(this.normalizer(option));
          });
        }
      }
    },

    /**
     * Normalizes the key-value pairs of each given list option.
     * @param {Object} node - option being normalized.
     * @returns {Object}
     */
    normalizer(node) {
      let id = "";
      let label = "";
      switch (true) {
        case node.hasOwnProperty("code"):
          id = node.code;
          label = this.getListElementLabel(node);
          break;
        case node.hasOwnProperty("or_id"): //OwnerList
          id = node.or_id;
          label = node.or_nom;
          break;
        case node.hasOwnProperty("pw_id"): //PathwayIdsList
          id = node.pw_id;
          label = node.pw_id;
          break;
      }
      return {
        id: id,
        label: label
      };
    },

    requiredWasMadeEmpty(objectIndex, field) {
      return field.required && !this.objects[objectIndex][field.var_name];
    },

    /**
     * Gets the original value of a given variable
     * @param {Number} objectIndex - index of the object for which the value is being changed
     * @param {Object} field - field being analysed
     * @param value
     * @return {String | Number}
     */
    setValue(objectIndex, field, value) {
      let object = this.objects[objectIndex];
      _.set(object, field.var_name, value);
      if (field.var_name.includes("_bals")) {
        this.updateTotalOfHPs();
      }
      if (objectIndex < this.numberOfOriginalObjects) {
        this.updateObjectsToEdit(objectIndex, field.var_name, value);
      } else {
        this.updateObjectToAdd(objectIndex, field.var_name, value);
      }
    },

    updateTotalOfHPs() {
      let totalHab = 0;
      let totalCom = 0;
      this.objects.forEach(object => {
        let habBals = object.cp_hab_bals;
        if (habBals !== undefined) {
          if (typeof habBals == "string") {
            habBals = parseInt(habBals);
          }
          totalHab += habBals;
        }
        let comBals = object.cp_com_bals;
        if (comBals !== undefined) {
          if (typeof comBals == "string") {
            comBals = parseInt(comBals);
          }
          totalCom += comBals;
        }
      });
      this.$emit("updateTotalOfHPs", { totalHab, totalCom });
    },

    /**
     * Places the new value in an array to send to the server.
     * @param {Number} objectIndex - index of the object for which the value is being changed
     * @param {String} varName
     * @param {String | Number} newValue
     */
    updateObjectsToEdit(objectIndex, varName, newValue) {
      let uuid = this.getObjectUUID(this.objects[objectIndex]);
      let newVarName = varName.replace(this.initials + "_", "");
      let editedObjectIndex = this.objectsToEdit.findIndex(
        editedObject => editedObject.uuid === uuid
      );
      if (newValue !== this.originalObjects[objectIndex][varName]) {
        if (editedObjectIndex === -1) {
          this.objectsToEdit.push({ uuid: uuid, [newVarName]: newValue });
        } else this.objectsToEdit[editedObjectIndex][newVarName] = newValue;
      } else if (editedObjectIndex !== -1) {
        if (Object.keys(this.objectsToEdit[editedObjectIndex]).length > 2) {
          delete this.objectsToEdit[editedObjectIndex][newVarName];
        } else {
          this.objectsToEdit.splice(editedObjectIndex, 1);
        }
      }
    },

    /**
     * Places the new value in an array of new object to send to the server.
     * @param {Number} objectIndex - index of the object for which the value is being changed
     * @param {String} varName
     * @param {String | Number} newValue
     */
    updateObjectToAdd(objectIndex, varName, newValue) {
      let newVarName = varName.replace(this.initials + "_", "");
      this.objectsToAdd[objectIndex - this.numberOfOriginalObjects][
        newVarName
      ] = newValue;
    },

    /**
     * Checks if the given input field is a standard one.
     * @param {String} inputType - type of input field.
     * @return {Boolean}
     */
    isStandardInput(inputType) {
      return (
        inputType === "text" ||
        inputType === "textarea" ||
        inputType === "number"
      );
    },

    /**
     * Gets the exact input type to use if it is a standard one.
     * @param {String} inputType - type of input field.
     * @return {String}
     */
    getSpecificStandardInputType(inputType) {
      if (inputType === "text" || inputType === "comment") {
        return "text";
      } else if (inputType === "number") return inputType;
    }
  },

  watch: {
    /**
     * Sends an update to the toolbar regarding the total number of objects.
     * @param {Number} newValue - new total of objects
     */
    numberObjects(newValue) {
      bus.$emit("updateNumberObjects", newValue);
      bus.$emit("updateNumberCustomerPremises", newValue);
    },

    /**
     * Sends an update to the toolbar regarding the number of select objects.
     * @param {Number} newValue - new number of selected objects
     */
    numberSelectedObjects(newValue) {
      bus.$emit("updateNumberSelectedObjects", newValue);
    }
  },

  destroyed() {
    bus.$off("save_customer_premises");
    bus.$off("add");
    bus.$off("delete");
    bus.$off("clone");
    bus.$off("selectAll");
    bus.$off("invertSelection");
    bus.$off("clearSelection");
  }
};
</script>



// WEBPACK FOOTER //
// src/components/Home/Forms/_SpecialTabs/CustomerPremises/CustomerPremisesTable.vue